import { useState, useCallback } from "react";

/**
 * Hook for smart pagination that handles real-time updates
 * Automatically adjusts pagination when new items are added
 */
export const useSmartPagination = (queryKey, options = {}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(options.itemsPerPage || 12);

  const {
    onPageChange: externalOnPageChange,
    onNewItemAdded,
    onItemUpdated,
    onItemRemoved,
  } = options;

  /**
   * Calculate total pages
   */
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      if (externalOnPageChange) {
        externalOnPageChange(page);
      }
    },
    [externalOnPageChange],
  );

  /**
   * Handle new item added
   */
  const handleNewItemAdded = useCallback(
    (newItem) => {
      setTotalItems((prev) => prev + 1);

      // If we're on the first page, we might need to adjust the current page
      if (currentPage === 1) {
        // New item will appear at the top, no page change needed
        return;
      }

      // If we're on a later page, check if we need to go back
      const newTotalPages = Math.ceil((totalItems + 1) / itemsPerPage);
      if (currentPage > newTotalPages) {
        // Go to the last page
        handlePageChange(newTotalPages);
      }

      if (onNewItemAdded) {
        onNewItemAdded(newItem);
      }
    },
    [currentPage, totalItems, itemsPerPage, handlePageChange, onNewItemAdded],
  );

  /**
   * Handle item updated
   */
  const handleItemUpdated = useCallback(
    (updatedItem) => {
      if (onItemUpdated) {
        onItemUpdated(updatedItem);
      }
    },
    [onItemUpdated],
  );

  /**
   * Handle item removed
   */
  const handleItemRemoved = useCallback(
    (removedItem) => {
      setTotalItems((prev) => Math.max(0, prev - 1));

      // Check if we need to adjust current page
      const newTotalPages = Math.ceil(
        Math.max(0, totalItems - 1) / itemsPerPage,
      );
      if (currentPage > newTotalPages && newTotalPages > 0) {
        // Go to the last page
        handlePageChange(newTotalPages);
      } else if (newTotalPages === 0) {
        // No items left, go to page 1
        handlePageChange(1);
      }

      if (onItemRemoved) {
        onItemRemoved(removedItem);
      }
    },
    [currentPage, totalItems, itemsPerPage, handlePageChange, onItemRemoved],
  );

  /**
   * Update total items from query data
   */
  const updateTotalItems = useCallback((data) => {
    if (data?.pagination?.total !== undefined) {
      setTotalItems((prev) => {
        const newTotal = data.pagination.total;
        if (prev !== newTotal) {
          return newTotal;
        }
        return prev;
      });
    }
  }, []);

  /**
   * Reset pagination
   */
  const resetPagination = useCallback(() => {
    setCurrentPage(1);
    setTotalItems(0);
  }, []);

  /**
   * Go to first page
   */
  const goToFirstPage = useCallback(() => {
    handlePageChange(1);
  }, [handlePageChange]);

  /**
   * Go to last page
   */
  const goToLastPage = useCallback(() => {
    handlePageChange(totalPages);
  }, [handlePageChange, totalPages]);

  /**
   * Go to next page
   */
  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  /**
   * Go to previous page
   */
  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, handlePageChange]);

  /**
   * Check if we can go to next page
   */
  const canGoNext = currentPage < totalPages;

  /**
   * Check if we can go to previous page
   */
  const canGoPrevious = currentPage > 1;

  /**
   * Get pagination info
   */
  const paginationInfo = {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    startIndex: (currentPage - 1) * itemsPerPage,
    endIndex: Math.min(currentPage * itemsPerPage, totalItems),
    canGoNext,
    canGoPrevious,
    isFirstPage: currentPage === 1,
    isLastPage: currentPage === totalPages,
    hasItems: totalItems > 0,
  };

  return {
    // State
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    paginationInfo,

    // Actions
    setCurrentPage: handlePageChange,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPreviousPage,
    resetPagination,
    updateTotalItems,

    // Event handlers for real-time updates
    handleNewItemAdded,
    handleItemUpdated,
    handleItemRemoved,
  };
};

export default useSmartPagination;
